import { Button } from "antd";
import React from "react";
import {
  addHttpToLink,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import { useDesign } from "../../context/DesignContext";
import { isHexDark } from "../../Utils/HelperFunction/HelperFunction";
import { darkenColor } from "../../Screens/DashboardView/StayUpToDate/LandingPageComponents/LinksComponent/Components/hoverUtils";
import { resolveShopButtonCta } from "../../Utils/normalizeShopUrl";

const ProductShopButton = (props: any) => {
  const {
    isLinks,
    isIndividual,
    type,
    post,
    isCarousel,
    isDashboard,
    onBeforeNavigate,
    asLink = true,
    designTokens: designTokensProp,
    shopLinkOverrides,
  } = props;
  const contextDesign = useDesign();
  const {
    fontFamily: contextFont,
    buttonBackground: contextButtonBg,
    templateName: contextTemplateName,
    tertiaryColor: contextTertiary,
    secondaryColor: contextSecondary,
    primaryColor: contextPrimary,
    forthColor: contextForth,
    primaryFont,
    secondaryFont,
  } = contextDesign;
  // When parent passes designTokens (e.g. carousel), use them so button matches theme
  const fontFamily = designTokensProp?.fontFamily ?? contextFont;
  const primaryFontFamily = designTokensProp?.primaryFontFamily ?? primaryFont;
  const secondaryFontFamily = designTokensProp?.secondaryFontFamily ?? secondaryFont;
  const templateName = designTokensProp?.templateName ?? contextTemplateName;
  const buttonBackground =
    designTokensProp?.forthColor ??
    contextButtonBg;
  const tertiaryColor = designTokensProp?.tertiaryColor ?? contextTertiary;
  const secondaryColor = designTokensProp?.secondaryColor ?? contextSecondary;
  const forthColor = designTokensProp?.forthColor ?? contextForth;
  // Priority: shop_link → Shop; external/tagged → View (Shop on products/isLinks carousels);
  // permalink/source → Watch
  const shopCta = resolveShopButtonCta(post, shopLinkOverrides, {
    forceShopLabel: Boolean(isLinks),
  });
  if (!shopCta) {
    return null;
  }
  // Treat as "default theme" only when the template is literally "default",
  // or when we're on the dashboard *without* explicit design tokens passed in.
  // If explicit designTokens are provided, let them fully control styling.
  const isDefaultTheme =
    (templateName || "").toLowerCase() === "default" ||
    (!designTokensProp && isDashboard);
  const lightenColor = (color: string, percent: number) => {
    const hex = color.replace("#", "");

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
    const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
    const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

    return `#${newR.toString(16).padStart(2, "0")}${newG
      .toString(16)
      .padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
  };

  const getHoverBg = (): string => {
    if (isDefaultTheme) return "#d1d2d5";
    const bg = (buttonBackground || "").replace(/!important/gi, "").trim();
    const normalized = bg.toLowerCase();
    if (!bg) return "#d1d2d5";
    // Only lighten if it's a hex color, otherwise fallback
    if (normalized.startsWith("#")) {
      if(isHexDark(bg))
      return lightenColor(bg, 20);
    else
      return darkenColor(bg, 10)
    }
    return "#d1d2d5";
  };

  const getButtonStyle = () => {
    const hoverBg = getHoverBg();
    const disableFontFamily = Boolean(isDashboard);
    // Always apply structural styles so layout doesn't break when designTokens are present
    const structuralStyle: React.CSSProperties = {
      fontWeight: 600,
      fontSize: "14px",
      padding: "6px 16px",
      borderRadius: 6,
      border: "none",
      boxShadow: "none",
      textAlign: "center",
    };
    const baseStyle: any = {
      // Media→Shop spacing comes from `.creator-post-stack` / `.link-url` gap
      // (or `.carousel-shop-btn-wrap` when Shop is not a flex sibling). Do not
      // add % margin here — YouTube zeroed it and IG/TikTok kept it, so Shop
      // sat at different Y positions in the same carousel row.
      marginTop: 0,
      "--hover-bg": hoverBg,
      ...structuralStyle,
      fontFamily: disableFontFamily ? undefined : secondaryFontFamily || undefined,
      // If explicit design tokens are provided, always use their background color
      // and ignore the dashboard grey override.
      // backgroundColor: isDefaultTheme
      //   ? "rgb(238, 238, 238)"
      //   : designTokensProp
      //   ? buttonBackground || "rgb(238, 238, 238)"
      //   : isDashboard
      //   ? "rgb(230, 231, 235)"
      //   : buttonBackground || "rgb(238, 238, 238)",
      color: isDefaultTheme ? "#000000" : (secondaryColor || "#000000"),
    };
    return baseStyle;
  };

  const buttonText = shopCta.label;
  const buttonClassName = `template-shop-btn-links${
    isIndividual ? "-individual" : isCarousel ? "-carousel" : ""
  } custom-button-with-hover shop-link`;
  const href = addHttpToLink(shopCta.url) || undefined;

  const openHref = () => {
    if (!href) return;
    openOutboundUrl(href);
  };

  // Use a real <a> so long-press on Android/iOS shows "Copy link" (big Shop buttons).
  if (href && asLink) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={buttonClassName}
        style={{ ...getButtonStyle(), textDecoration: "none", display: "inline-block" }}
        onClick={() => onBeforeNavigate?.()}
      >
        {buttonText}
      </a>
    );
  }

  // When already inside a parent <a> (asLink=false), use span so long-press shows parent link menu.
  // If a caller forgets the parent link (YouTube CTA bug), still navigate so Shop is never dead.
  if (href && !asLink) {
    return (
      <span
        className={buttonClassName}
        style={{ ...getButtonStyle(), cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          onBeforeNavigate?.();
          const parentLink = (e.currentTarget as HTMLElement).closest("a[href]");
          if (parentLink) return; // let the parent <a> handle navigation
          e.preventDefault();
          e.stopPropagation();
          openHref();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          onBeforeNavigate?.();
          const parentLink = (e.currentTarget as HTMLElement).closest("a[href]");
          if (parentLink) {
            (parentLink as HTMLAnchorElement).click();
            return;
          }
          openHref();
        }}
      >
        {buttonText}
      </span>
    );
  }

  return (
    <Button className={buttonClassName} style={getButtonStyle()}>
      {buttonText}
    </Button>
  );
};

export default ProductShopButton;
